import type {
  AdminRole,
  AIFlag,
  AppealStatus,
  Channel,
  Priority,
  Resolution,
  SubmissionStatus,
  SubmissionType,
} from '../types';

// Channel definitions
export const CHANNELS: Record<Channel, { label: string; emoji: string }> = {
  ios: { label: 'iOS', emoji: '📱' },
  tvos: { label: 'tvOS', emoji: '📺' },
  service: { label: 'Service', emoji: '🌐' },
} as const;

export const ACTIVE_CHANNELS: Channel[] = ['ios', 'tvos']; // service disabled in v1

// Submission types
export const SUBMISSION_TYPES: Record<SubmissionType, { label: string; emoji: string }> = {
  bug_report: { label: 'Баг-репорт', emoji: '🐛' },
  feature_request: { label: 'Запрос фичи', emoji: '💡' },
} as const;

// Submission statuses
export const SUBMISSION_STATUSES: Record<
  SubmissionStatus,
  { label: string; emoji: string; color: string }
> = {
  pending: { label: 'Ожидает', emoji: '🟡', color: 'yellow' },
  approved: { label: 'Одобрено', emoji: '✅', color: 'green' },
  rejected: { label: 'Отклонено', emoji: '❌', color: 'red' },
  cancelled: { label: 'Отменено', emoji: '⚪', color: 'gray' },
} as const;

// Alias for convenience
export const STATUSES = SUBMISSION_STATUSES;

// Resolutions
export const RESOLUTIONS: Record<Resolution, { label: string; emoji: string }> = {
  in_progress: { label: 'В работе', emoji: '🔄' },
  resolved: { label: 'Решено', emoji: '✅' },
  wont_fix: { label: 'Не будет исправлено', emoji: '🚫' },
  duplicate: { label: 'Дубликат', emoji: '📋' },
  invalid: { label: 'Некорректно', emoji: '❓' },
} as const;

// Priorities
export const PRIORITIES: Record<Priority, { label: string; emoji: string; color: string }> = {
  low: { label: 'Низкий', emoji: '🔵', color: 'blue' },
  medium: { label: 'Средний', emoji: '🟡', color: 'yellow' },
  high: { label: 'Высокий', emoji: '🟠', color: 'orange' },
  critical: { label: 'Критический', emoji: '🔴', color: 'red' },
} as const;

// AI flags
export const AI_FLAGS: Record<AIFlag, { label: string; emoji: string }> = {
  toxic: { label: 'Токсичность', emoji: '⚠️' },
  duplicate: { label: 'Дубликат', emoji: '🔍' },
  spam: { label: 'Спам', emoji: '🚫' },
} as const;

// Admin roles
export const ADMIN_ROLES: Record<AdminRole, { label: string; emoji: string; level: number }> = {
  owner: { label: 'Владелец', emoji: '👑', level: 3 },
  admin: { label: 'Админ', emoji: '⭐', level: 2 },
  maintainer: { label: 'Модератор', emoji: '🔧', level: 1 },
} as const;

// Appeal statuses
export const APPEAL_STATUSES: Record<AppealStatus, { label: string; emoji: string }> = {
  pending: { label: 'Ожидает', emoji: '🟡' },
  approved: { label: 'Одобрено', emoji: '✅' },
  rejected: { label: 'Отклонено', emoji: '❌' },
} as const;

// Ticket ID format
export const TICKET_ID_PREFIX = 'KH';
export const TICKET_ID_LENGTH = 5; // KH-XXXXX

// Rate limits
export const DEFAULT_DAILY_SUBMISSION_LIMIT = 10;

// Session/token settings
export const MAGIC_LINK_EXPIRY_MINUTES = 5;
export const SESSION_EXPIRY_HOURS = 24;

// AI settings
export const AI_MODEL = 'z-ai/glm-4.5-air:free';
export const AI_MAX_TOKENS = 1024;
export const AI_TEMPERATURE = 0.3;
export const AI_TOXICITY_THRESHOLD = 0.7;
export const AI_DUPLICATE_THRESHOLD = 0.7;

// GitHub labels
export const GITHUB_LABELS = {
  channels: {
    ios: 'ios',
    tvos: 'tvos',
    service: 'service',
  },
  types: {
    bug_report: 'bug',
    feature_request: 'feature',
  },
  priorities: {
    low: 'priority:low',
    medium: 'priority:medium',
    high: 'priority:high',
    critical: 'priority:critical',
  },
} as const;
